/**
 * Responsive grid of player cards with animations.
 *
 * The `tv` layout measures its container and picks the column count that gives
 * the largest card, so the cards always fill the available space without scrolling.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlayerCard } from '@/components/medium/PlayerCard'
import { Player } from '@/stores/playerStore'
import { bestFitGrid } from './bestFitGrid'

interface PlayerGridProps {
  players: Player[]
  currentPlayerId?: string
  pingingPlayerIds?: string[]
  layout?: 'phone' | 'tv'
}

interface ContainerSize {
  width: number
  height: number
}

const PHONE_GRID_CLASSES = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8'

/** Card width / height on TV. */
const TV_CARD_ASPECT = 4 / 5
const TV_GAP_PX = 24

function EmptyGrid({ fill }: { fill: boolean }) {
  const { t } = useTranslation()
  return (
    <div
      className={`flex flex-col items-center justify-center space-y-4 opacity-20 ${
        fill ? 'h-full' : 'h-64'
      }`}
    >
      <div className="w-12 h-12 border-2 border-border border-t-primary rounded-full animate-spin" />
      <p className="text-xs uppercase tracking-[0.5em] text-muted-foreground">
        {t('lobby.scanningForUnits')}
      </p>
    </div>
  )
}

function TvPlayerGrid({
  players,
  currentPlayerId,
  pingingPlayerIds,
}: {
  players: Player[]
  currentPlayerId?: string
  pingingPlayerIds: string[]
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0 })

  useEffect(() => {
    const element = containerRef.current
    if (element === null) return
    // contentRect is the layout size, unaffected by the TvCanvas scale transform.
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
      }
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const fit = bestFitGrid(size.width, size.height, players.length, TV_CARD_ASPECT, TV_GAP_PX)
  // Whole pixels: fractional widths can round up and push the last card to a new row.
  const cardWidth = fit === null ? 0 : Math.floor(fit.cardWidth)
  const cardHeight = fit === null ? 0 : Math.floor(fit.cardHeight)

  return (
    <div ref={containerRef} className="h-full w-full flex items-center justify-center">
      {fit !== null && (
        <div
          className="flex flex-wrap justify-center"
          style={{
            gap: TV_GAP_PX,
            width: fit.columns * cardWidth + (fit.columns - 1) * TV_GAP_PX,
          }}
        >
          {players.map((player) => (
            <div
              key={player.id}
              className="animate-in fade-in slide-in-from-bottom-4 duration-500"
              style={{ width: cardWidth, height: cardHeight }}
            >
              <PlayerCard
                player={player}
                isCurrentUser={player.id === currentPlayerId}
                isPinging={pingingPlayerIds.includes(player.id)}
                variant="tv"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function PlayerGrid({
  players,
  currentPlayerId,
  pingingPlayerIds = [],
  layout = 'phone',
}: PlayerGridProps) {
  if (layout === 'tv') {
    const phonePlayers = players.filter((player) => player.role !== 'tv')
    if (phonePlayers.length === 0) return <EmptyGrid fill />
    return (
      <TvPlayerGrid
        players={phonePlayers}
        currentPlayerId={currentPlayerId}
        pingingPlayerIds={pingingPlayerIds}
      />
    )
  }

  if (players.length === 0) return <EmptyGrid fill={false} />

  return (
    <div className={`grid ${PHONE_GRID_CLASSES}`}>
      {players.map((player) => (
        <div key={player.id} className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          <PlayerCard
            player={player}
            isCurrentUser={player.id === currentPlayerId}
            isPinging={pingingPlayerIds.includes(player.id)}
          />
        </div>
      ))}
    </div>
  )
}
